/**
 * Currency Converter - Frontend Version
 * Tassi di conversione EUR-based (allineati al backend)
 */

export const CURRENCY_RATES = {
  EUR: 1, // Valuta base
  XOF: 655.96, // 1 EUR = 655.96 XOF
  USD: 1.08 // 1 EUR = 1.08 USD
}

export const CURRENCY_SYMBOLS = {
  XOF: 'XOF',
  EUR: '€',
  USD: '$'
}

/**
 * Converte un importo da una valuta a un'altra
 */
export function convertCurrency (amount, fromCurrency, toCurrency) {
  if (!amount || isNaN(amount)) return 0
  if (fromCurrency === toCurrency) return amount

  // Converti prima a EUR (valuta base)
  const amountEUR = fromCurrency === 'EUR'
    ? amount
    : amount / CURRENCY_RATES[fromCurrency]

  // Converti da EUR alla valuta target
  const result = toCurrency === 'EUR'
    ? amountEUR
    : amountEUR * CURRENCY_RATES[toCurrency]

  return parseFloat(result.toFixed(2))
}

/**
 * @deprecated Usa convertCurrency(amount, fromCurrency, 'XOF') invece
 * Converte un importo a XOF
 */
export function convertToXOF (amount, fromCurrency = 'XOF') {
  console.warn('convertToXOF è deprecata, usa convertCurrency() invece')
  return convertCurrency(amount, fromCurrency, 'XOF')
}

/**
 * @deprecated Usa convertCurrency(amountXOF, 'XOF', toCurrency) invece
 * Converte da XOF a un'altra valuta
 */
export function convertFromXOF (amountXOF, toCurrency = 'XOF') {
  console.warn('convertFromXOF è deprecata, usa convertCurrency() invece')
  return convertCurrency(amountXOF, 'XOF', toCurrency)
}

/**
 * Formatta un importo con separatore migliaia
 * XOF e altre valute senza centesimi non mostrano i decimali
 */
export function formatAmount (amount, currency = 'XOF') {
  if (!amount || isNaN(amount)) amount = 0

  // Valute senza decimali (franchi CFA, yen, won, etc.)
  const currenciesWithoutDecimals = ['XOF', 'XAF', 'JPY', 'KRW', 'VND', 'CLP']
  const decimals = currenciesWithoutDecimals.includes(currency) ? 0 : 2

  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(amount)
}

/**
 * Formatta con simbolo valuta
 * Es: "1.000,00 €" o "1.000 CFA" (senza decimali per XOF)
 */
export function formatCurrency (amount, currency = 'XOF') {
  const formatted = formatAmount(amount, currency)
  const symbol = CURRENCY_SYMBOLS[currency] || 'XOF'
  return `${formatted} ${symbol}`
}

/**
 * Formatta mostrando il prezzo con conversione alla valuta preferita
 * - Se è già nella valuta preferita → mostra SOLO quel prezzo
 * - Se è in valuta diversa → mostra "originale (≈ convertito)"
 *
 * Esempi con valuta preferita = XOF:
 * - 65.596 XOF → "65.596 CFA"
 * - 100 EUR → "100,00 € (≈ 65.596 CFA)"
 *
 * La valuta preferita viene letta da localStorage (user.azienda.valuta)
 */
export function formatCurrencyWithPreferred (amount, currency) {
  if (!amount || isNaN(amount)) amount = 0

  // Leggi valuta preferita dell'azienda da localStorage
  let preferredCurrency = 'EUR' // Default
  try {
    const user = JSON.parse(window.localStorage.getItem('user'))
    if (user?.azienda?.valuta) {
      preferredCurrency = user.azienda.valuta
      // Converti simbolo vecchio a codice ISO
      if (preferredCurrency === '€') preferredCurrency = 'EUR'
    }
  } catch (error) {
    console.warn('Impossibile leggere valuta preferita, uso EUR di default')
  }

  const original = formatCurrency(amount, currency)

  // Se è già nella valuta preferita, mostra SOLO quella (senza conversione)
  if (currency === preferredCurrency) {
    return original
  }

  // Altrimenti mostra originale + conversione alla valuta preferita
  const amountConverted = convertCurrency(amount, currency, preferredCurrency)
  const converted = formatCurrency(amountConverted, preferredCurrency)

  return `${original} (≈ ${converted})`
}

/**
 * @deprecated Usa formatCurrencyWithPreferred() invece
 * Formatta mostrando sia valuta originale che XOF (hardcoded)
 */
export function formatCurrencyWithXOF (amount, currency = 'XOF') {
  console.warn('formatCurrencyWithXOF è deprecata, usa formatCurrencyWithPreferred() invece')
  if (!amount || isNaN(amount)) amount = 0

  const original = formatCurrency(amount, currency)

  // Se è già XOF, non mostrare conversione
  if (currency === 'XOF') return original

  const amountXOF = convertCurrency(amount, currency, 'XOF')
  const converted = formatCurrency(amountXOF, 'XOF')

  return `${original} (≈ ${converted})`
}

/**
 * Ottiene il simbolo della valuta
 */
export function getCurrencySymbol (currency = 'XOF') {
  return CURRENCY_SYMBOLS[currency] || 'XOF'
}

/**
 * Mostra un prezzo (in EUR) nella valuta dell'azienda
 *
 * Questo è il metodo principale da usare per mostrare prezzi nel gestionale.
 * Converte automaticamente da EUR alla valuta dell'azienda e formatta.
 *
 * @param {number} priceEUR - Prezzo in EUR (dalla API backend)
 * @param {Object} azienda - Oggetto azienda con campo 'valuta'
 * @returns {string} - Prezzo formattato (es: "65.596 CFA")
 *
 * @example
 * const priceDisplay = await displayInAziendaValuta(100, azienda)
 * // Se azienda.valuta = 'XOF' → "65.596 CFA"
 * // Se azienda.valuta = 'EUR' → "100,00 €"
 */
export function displayInAziendaValuta (priceEUR, azienda) {
  if (!priceEUR || isNaN(priceEUR)) {
    const valutaAzienda = azienda?.valuta || 'EUR'
    return formatCurrency(0, valutaAzienda)
  }

  const valutaAzienda = azienda?.valuta || 'EUR'
  const convertedAmount = convertCurrency(priceEUR, 'EUR', valutaAzienda)
  return formatCurrency(convertedAmount, valutaAzienda)
}
